import { ReactNode } from "react";
import { Box, Link, Stack, useMediaQuery, useTheme } from "@mui/material";
import { OptionalLink } from "src/components-mui/base/optional-link/optional-link";
import { Typography } from "src/components-mui/base/typography/typography";
import { WatchlistButton } from "src/components-mui/common/buttons/watchlist-button/watchlist-button";
import { HoverCard } from "src/components-mui/common/hover-card/hover-card";
import { PerformanceValue } from "src/components-mui/common/performance-value/performance-value";
import { TraderAvatar } from "src/components-mui/common/trader-avatar/trader-avatar";
import { mapTraderToAvatarUser } from "src/components-mui/common/trader-avatar/trader-avatar.mappers";
import { IWikifolioCardData } from "src/components-mui/common/wikifolio-card/wikifolio-card.types";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";

const CARD_LAYOUT_SX = { alignItems: "center", gap: 3, fontSize: "sm" };

interface IWikifolioCardContainerProps {
    cardLink: string | null;
    children: ReactNode;
}

const WikifolioCardContainer = ({ cardLink, children }: IWikifolioCardContainerProps) => {
    if (!cardLink) {
        return (
            <Stack
                component="article"
                direction="row"
                sx={[CARD_LAYOUT_SX, { p: 2, borderRadius: "8px", backgroundColor: "common.white", boxShadow: 2 }]}
            >
                {children}
            </Stack>
        );
    }

    return (
        <Link href={cardLink} variant="unstyled" sx={{ display: "block", borderRadius: 1 }}>
            <HoverCard component="article" sx={[CARD_LAYOUT_SX, { "&:hover": { boxShadow: 2 } }]}>
                {children}
            </HoverCard>
        </Link>
    );
};

interface IWikifolioCardProps {
    wikifolio: IWikifolioCardData;
}

export const WikifolioCard = ({ wikifolio }: IWikifolioCardProps) => {
    const { buildWikifolioLink, buildTraderProfileLink } = useLinkBuilder();
    const getUserName = useGetUserName();
    const theme = useTheme();
    const isCardLink = useMediaQuery(theme.breakpoints.down("sm"));
    const { id, symbol, shortDescription, trader, ranking, isOnWatchlist } = wikifolio;
    const traderAvatarUser = mapTraderToAvatarUser({ trader });
    const wikifolioLink = buildWikifolioLink(symbol);
    const traderProfileLink = isCardLink || trader.isDeleted ? undefined : buildTraderProfileLink(trader.nickName);

    return (
        <WikifolioCardContainer cardLink={isCardLink ? wikifolioLink : null}>
            <Stack direction="row" sx={{ flex: 1, minWidth: 0, gap: 1, alignItems: "flex-start" }}>
                <TraderAvatar trader={traderAvatarUser} size="sm" href={traderProfileLink} sx={{ flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                    <OptionalLink href={isCardLink ? undefined : wikifolioLink} underline="hover" sx={{ display: "block", color: "gray.800" }}>
                        <Typography component="span" noWrap sx={{ display: "block", color: "gray.800", fontSize: "sm", fontWeight: "bold" }}>
                            {shortDescription}
                        </Typography>
                    </OptionalLink>
                    <OptionalLink href={traderProfileLink} underline="hover" sx={{ display: "block", color: "gray.800" }}>
                        <Typography component="span" noWrap sx={{ display: "block", color: "gray.800", fontSize: "sm", fontWeight: "normal" }}>
                            {getUserName(trader)}
                        </Typography>
                    </OptionalLink>
                </Box>
            </Stack>
            <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <PerformanceValue component="p" value={ranking.value} sx={{ fontSize: "sm", fontWeight: "bold" }} />
                <Typography component="p" sx={{ fontSize: "sm", color: "gray.500" }}>
                    {ranking.label}
                </Typography>
            </Box>
            {!isCardLink && (
                <WatchlistButton
                    wikifolioId={id}
                    wikifolioShortDescription={shortDescription}
                    isOnWatchlist={isOnWatchlist}
                    omitTitle
                    size="small"
                    sx={{ display: { xs: "none", sm: "inline-flex" }, flexShrink: 0 }}
                />
            )}
        </WikifolioCardContainer>
    );
};
